import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../config';

const TinyWinsArchitect = ({ mode = 'normal', previousJourneyId = null, currentJourney = null, onComplete, onCancel }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [journeyContract, setJourneyContract] = useState(null);
  const messagesEndRef = useRef(null);

  // Get the appropriate title based on mode
  const getTitle = () => {
    switch (mode) {
      case 'rescue': return 'Adjustment Mode';
      case 'evolve': return 'Level Up Mode';
      case 'adjust': return 'Refine Your Habit';
      case 'returning': return 'Your Next Chapter';
      default: return 'Tiny Wins Architect';
    }
  };

  const getSubtitle = () => {
    switch (mode) {
      case 'rescue': return "Let's make this easier";
      case 'evolve': return "Ready to grow!";
      case 'adjust': return "Fine-tune your tiny win";
      case 'returning': return "Welcome back — let's build on your wins";
      default: return 'Your compassionate coach';
    }
  };

  // Start conversation with initial greeting
  useEffect(() => {
    startConversation();
  }, [mode]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const startConversation = async () => {
    setLoading(true);
    
    // Create context message based on mode and existing journey
    let contextMessage = "Hello, I'm ready to start.";
    if (mode === 'rescue' && currentJourney) {
      contextMessage = `I'm struggling with my current habit. My tiny win is "${currentJourney.tiny_win}" with anchor "${currentJourney.anchor_habit}". It's been hard to stick with.`;
    } else if (mode === 'evolve' && currentJourney) {
      contextMessage = `I've completed ${currentJourney.target_days || 14} days of "${currentJourney.tiny_win}"! I'm ready to level up. My identity goal is "${currentJourney.identity_goal}".`;
    } else if (mode === 'adjust' && currentJourney) {
      contextMessage = `I want to adjust my current habit. Here's my current setup:
- Identity goal: "${currentJourney.identity_goal}"
- Target habit: "${currentJourney.target_habit}"  
- Current tiny win: "${currentJourney.tiny_win}"
- Anchor habit: "${currentJourney.anchor_habit}"
I'd like to refine or change this. Can you help me?`;
    } else if (currentJourney) {
      // Even in 'normal' mode, if there's an existing journey, acknowledge it
      contextMessage = `I already have a journey set up. My current tiny win is "${currentJourney.tiny_win}". I want to start a completely new habit journey.`;
    }
    
    try {
      const res = await axios.post(`${API}/architect/chat`, {
        message: contextMessage,
        conversation_id: null,
        mode: mode
      });
      setConversationId(res.data.conversation_id);
      setMessages([{ role: 'assistant', content: res.data.response }]);
      if (res.data.journey_contract) {
        setJourneyContract(res.data.journey_contract);
      }
    } catch (err) {
      const fallbackMessage = mode === 'rescue' 
        ? "I hear you're finding things difficult. That takes courage to admit. Let's make this easier together. What specifically feels hard about your current habit?"
        : mode === 'evolve'
        ? "Congratulations on completing your journey! You've proven you can show up consistently. Let's talk about leveling up. What does the next step look like for you?"
        : "Welcome! Let's start with the big picture. Complete this sentence: 'I want to become the type of person who...'";
      setMessages([{ role: 'assistant', content: fallbackMessage }]);
    }
    setLoading(false);
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/architect/chat`, {
        message: userMessage,
        conversation_id: conversationId,
        mode: mode
      });
      
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      
      if (res.data.journey_contract) {
        setJourneyContract(res.data.journey_contract);
      }
    } catch (err) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting. Please try again." 
      }]);
    }
    setLoading(false);
  };

  const saveJourney = async () => {
    if (!journeyContract) return;
    
    setLoading(true);
    try {
      // Include previous journey ID for chaining
      const journeyData = { ...journeyContract };
      if (previousJourneyId) {
        journeyData.previous_journey_id = previousJourneyId;
      }
      await axios.post(`${API}/journey`, journeyData);
      onComplete();
    } catch (err) {
      alert('Failed to save journey. Please try again.');
    }
    setLoading(false);
  };

  // Clean up the response text (remove JSON markers for display)
  const cleanResponse = (text) => {
    return text
      .replace(/===JOURNEY_CONTRACT===[\s\S]*?===END_CONTRACT===/g, '')
      .trim();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              mode === 'rescue' ? 'bg-amber-100' : mode === 'evolve' ? 'bg-purple-100' : 'bg-emerald-100'
            }`}>
              <span className="text-xl">{mode === 'rescue' ? '🔧' : mode === 'evolve' ? '🚀' : '🏗️'}</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">{getTitle()}</h1>
              <p className="text-xs text-gray-500">{getSubtitle()}</p>
            </div>
          </div>
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              Cancel
            </button>
          )}
        </div>
      </header>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white shadow-md text-gray-800'
                }`}
              >
                <p className="whitespace-pre-wrap">{cleanResponse(msg.content)}</p>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white shadow-md rounded-2xl px-4 py-3">
                <p className="text-sm text-emerald-600 font-medium mb-2">Analyzing your goals...</p>
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Journey Contract Card (if ready) */}
      {journeyContract && (
        <div className="px-4 py-4 bg-white border-t">
          <div className="max-w-3xl mx-auto">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <h3 className="font-semibold text-emerald-800 mb-3 flex items-center">
                <span className="mr-2">📜</span> Your Journey Contract
              </h3>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium text-gray-700">Identity:</span> {journeyContract.identity_goal}</p>
                <p><span className="font-medium text-gray-700">Target:</span> {journeyContract.target_habit}</p>
                <p><span className="font-medium text-gray-700">Tiny Win:</span> {journeyContract.tiny_win}</p>
                <p><span className="font-medium text-gray-700">Anchor:</span> {journeyContract.anchor_habit}</p>
              </div>
              <button
                onClick={saveJourney}
                disabled={loading}
                className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg transition disabled:opacity-50"
              >
                {loading ? 'Saving...' : '✓ Start My Journey'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="bg-white border-t px-4 py-4">
        <form onSubmit={sendMessage} className="max-w-3xl mx-auto flex space-x-3">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your response..."
            disabled={loading}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none disabled:bg-gray-100"
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-medium transition disabled:opacity-50"
          >
            Send
          </button>
        </form>
      </div>
    </div>
  );
};

export default TinyWinsArchitect;
